import React, { useState, useEffect } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useParams } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Flex,
  Icon,
  Textarea,
  Spinner,
  Alert,
  Badge,
  Link,
  List,
  Separator,
  Field,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/chakra-v2-compat';
import { FaCheck, FaTimes, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaBus, FaExternalLinkAlt, FaMusic } from 'react-icons/fa';
import { encuestaPublicaAPI } from '../services/api';
import ActuacionMapPreview from './ActuacionMapPreview.jsx';
import AutobusInfoAccordion from './AutobusInfoAccordion.jsx';

export default function EncuestaPublica() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [asiste, setAsiste] = useState(null);
  const [necesitaAutobus, setNecesitaAutobus] = useState(false);
  const [notas, setNotas] = useState('');
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await encuestaPublicaAPI.get(token);
        setData(res.data);
        if (res.data.yaRespondido) {
          setAsiste(res.data.asiste);
          setNecesitaAutobus(res.data.necesitaAutobus || false);
          setNotas(res.data.notas || '');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Enlace no válido');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleSubmit = async () => {
    if (asiste === null) return;
    try {
      setSubmitting(true);
      const pAuto = data.preguntaAutobus === true || data.preguntaAutobus === 1;
      const payloadNecesitaAutobus = pAuto && asiste === true ? Boolean(necesitaAutobus) : null;
      await encuestaPublicaAPI.respond(token, {
        asiste,
        necesitaAutobus: payloadNecesitaAutobus,
        notas,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Flex minH="100vh" bg={bg} justify="center" align="center">
        <Spinner size="xl" color="purple.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex minH="100vh" bg={bg} justify="center" align="center" px={4}>
        <Card.Root bg={cardBg} maxW="400px" w="100%" shadow="xl" rounded="2xl">
          <Card.Body textAlign="center" py={10}>
            <Icon boxSize={12} color="red.400" mb={4} asChild><FaTimes /></Icon>
            <Heading size="md" mb={2} color="red.600">Error</Heading>
            <Text color="gray.500">{error}</Text>
          </Card.Body>
        </Card.Root>
      </Flex>
    );
  }

  if (submitted) {
    return (
      <Flex minH="100vh" bg={bg} justify="center" align="center" px={4}>
        <Card.Root bg={cardBg} maxW="400px" w="100%" shadow="xl" rounded="2xl">
          <Card.Body textAlign="center" py={10}>
            <Icon boxSize={12} color="green.400" mb={4} asChild><FaCheck /></Icon>
            <Heading size="md" mb={2} color="green.600">Respuesta enviada</Heading>
            <Text color="gray.500">Gracias, {data.musicoNombre}. Tu respuesta ha sido registrada.</Text>
          </Card.Body>
        </Card.Root>
      </Flex>
    );
  }

  const isClosed = data.encuestaEstado === 'CERRADA';
  const preguntaAutobus = data.preguntaAutobus === true || data.preguntaAutobus === 1;

  return (
    <Flex minH="100vh" bg={bg} justify="center" align="start" px={4} py={8}>
      <Card.Root bg={cardBg} maxW="500px" w="100%" shadow="xl" rounded="2xl">
        <Card.Body>
          <VStack gap={5} align="stretch">
            <Flex justify="center">
              <Avatar.Root size="lg"><Avatar.Fallback name={data.bandaNombre} /><Avatar.Image src={data.bandaAvatar} /></Avatar.Root>
            </Flex>

            <VStack gap={1}>
              <Text fontSize="sm" color="gray.500">{data.bandaNombre}</Text>
              <Heading size="md" textAlign="center">{data.actuacion}</Heading>
            </VStack>

            <VStack gap={2} bg="purple.50" p={4} rounded="xl">
              {data.fecha && <HStack><Icon color="purple.500" asChild><FaCalendarAlt /></Icon><Text fontSize="sm">{new Date(data.fecha).toLocaleDateString()}</Text></HStack>}
              {data.horario && <HStack><Icon color="purple.500" asChild><FaClock /></Icon><Text fontSize="sm">{data.horario}</Text></HStack>}
              {data.ubicacion ? (
                <HStack align="start">
                  <Icon color="purple.500" mt={1} asChild><FaMapMarkerAlt /></Icon>
                  <Box>
                    <Text fontSize="xs" fontWeight="700" color="gray.600">
                      Inicio
                    </Text>
                    <Text fontSize="sm">{data.ubicacion}</Text>
                  </Box>
                </HStack>
              ) : null}
              {data.ubicacionFin ? (
                <HStack align="start">
                  <Icon color="red.400" mt={1} asChild><FaMapMarkerAlt /></Icon>
                  <Box>
                    <Text fontSize="xs" fontWeight="700" color="gray.600">
                      Llegada
                    </Text>
                    <Text fontSize="sm">{data.ubicacionFin}</Text>
                  </Box>
                </HStack>
              ) : null}
              {(() => {
                const hasIni = data.lat != null && data.lng != null && !Number.isNaN(Number(data.lat)) && !Number.isNaN(Number(data.lng));
                const hasFin =
                  data.latFin != null &&
                  data.lngFin != null &&
                  !Number.isNaN(Number(data.latFin)) &&
                  !Number.isNaN(Number(data.lngFin));
                const hasBus =
                  preguntaAutobus &&
                  data.autobusLat != null &&
                  data.autobusLng != null &&
                  !Number.isNaN(Number(data.autobusLat)) &&
                  !Number.isNaN(Number(data.autobusLng));
                if (!hasIni && !hasFin && !hasBus) return null;
                return (
                  <Box w="full" rounded="md" overflow="hidden" borderWidth="1px" borderColor="purple.100" bg="white">
                    <ActuacionMapPreview
                      lat={data.lat}
                      lng={data.lng}
                      latFin={hasFin ? data.latFin : null}
                      lngFin={hasFin ? data.lngFin : null}
                      latAutobus={hasBus ? data.autobusLat : null}
                      lngAutobus={hasBus ? data.autobusLng : null}
                      height="200px"
                    />
                  </Box>
                );
              })()}
              {data.lat != null && data.lng != null && !Number.isNaN(Number(data.lat)) && !Number.isNaN(Number(data.lng)) ? (
                <Link
                  href={`https://www.openstreetmap.org/?mlat=${data.lat}&mlon=${data.lng}#map=16/${data.lat}/${data.lng}`}
                  fontSize="sm"
                  color="purple.600"
                  fontWeight="600"
                  target='_blank'
                  rel='noopener noreferrer'>
                  Inicio en mapa <Icon mx={1} asChild><FaExternalLinkAlt /></Icon>
                </Link>
              ) : null}
              {data.latFin != null && data.lngFin != null && !Number.isNaN(Number(data.latFin)) && !Number.isNaN(Number(data.lngFin)) ? (
                <Link
                  href={`https://www.openstreetmap.org/?mlat=${data.latFin}&mlon=${data.lngFin}#map=16/${data.latFin}/${data.lngFin}`}
                  fontSize="sm"
                  color="red.600"
                  fontWeight="600"
                  target='_blank'
                  rel='noopener noreferrer'>
                  Llegada en mapa <Icon mx={1} asChild><FaExternalLinkAlt /></Icon>
                </Link>
              ) : null}
              {preguntaAutobus &&
              data.autobusLat != null &&
              data.autobusLng != null &&
              !Number.isNaN(Number(data.autobusLat)) &&
              !Number.isNaN(Number(data.autobusLng)) ? (
                <Link
                  href={`https://www.openstreetmap.org/?mlat=${data.autobusLat}&mlon=${data.autobusLng}#map=16/${data.autobusLat}/${data.autobusLng}`}
                  fontSize="sm"
                  color="blue.600"
                  fontWeight="600"
                  target='_blank'
                  rel='noopener noreferrer'>
                  Autobús en mapa <Icon mx={1} asChild><FaExternalLinkAlt /></Icon>
                </Link>
              ) : null}
              {data.uniforme && (
                <HStack align="start">
                  <Text fontSize="xs" fontWeight="700" color="gray.600" minW="72px">Uniforme</Text>
                  <Text fontSize="sm">{data.uniforme}</Text>
                </HStack>
              )}
              {preguntaAutobus ? (
                <Box w="full" mt={1}>
                  <AutobusInfoAccordion
                    ubicacion={data.autobusUbicacion}
                    horaSalida={data.autobusHoraSalida}
                    lat={data.autobusLat}
                    lng={data.autobusLng}
                  />
                </Box>
              ) : null}

              {(Array.isArray(data.repertorioLineas) && data.repertorioLineas.length > 0) ||
              data.repertorioCompartidoUrl ? (
                <>
                  <Separator borderColor="purple.200" />
                  <Box w="full">
                    <HStack gap={2} mb={2}>
                      <Icon color="purple.600" asChild><FaMusic /></Icon>
                      <Text fontSize="xs" fontWeight="800" color="purple.800" letterSpacing="0.06em">
                        REPERTORIO
                      </Text>
                    </HStack>
                    {Array.isArray(data.repertorioLineas) && data.repertorioLineas.length > 0 ? (
                      <List.Root gap={1} pl={0} listStyleType="none" fontSize="sm" color="gray.700" mb={3}>
                        {data.repertorioLineas.map((line, i) => (
                          <List.Item key={`rep-${i}`} pl={0}>
                            {line}
                          </List.Item>
                        ))}
                      </List.Root>
                    ) : null}
                    {data.repertorioCompartidoUrl ? (
                      <Link
                        href={data.repertorioCompartidoUrl}
                        display="inline-flex"
                        alignItems="center"
                        gap={2}
                        fontSize="sm"
                        fontWeight="700"
                        color="purple.700"
                        target='_blank'
                        rel='noopener noreferrer'>
                        <Icon asChild><FaExternalLinkAlt /></Icon>
                        Ver partituras (repertorio compartido)
                      </Link>
                    ) : null}
                  </Box>
                </>
              ) : null}
            </VStack>

            <Box textAlign="center">
              <Text fontSize="lg" fontWeight="600">Hola, {data.musicoNombre}</Text>
              <Text fontSize="sm" color="gray.500">¿Vas a asistir a esta actuación?</Text>
            </Box>

            {isClosed ? (
              <Alert.Root status="warning" rounded="lg"><Alert.Indicator />La encuesta está cerrada. No se pueden enviar más respuestas.</Alert.Root>
            ) : data.yaRespondido ? (
              <VStack gap={3} align="stretch">
                <Alert.Root status="success" rounded="lg">
                  <Alert.Indicator />
                  Tu respuesta ya está registrada. No se puede volver a votar.
                </Alert.Root>
                <Box bg="gray.50" p={4} rounded="lg" borderWidth="1px" borderColor="gray.100">
                  <Text fontSize="sm" color="gray.500" mb={1}>Tu respuesta</Text>
                  <Text fontWeight="700" color={data.asiste ? 'green.600' : 'red.600'}>
                    {data.asiste ? 'Sí, asisto' : 'No asisto'}
                  </Text>
                  {preguntaAutobus && data.asiste ? (
                    <Text fontSize="sm" color="gray.600" mt={2}>
                      Autobús: {data.necesitaAutobus ? 'sí' : 'no'}
                    </Text>
                  ) : null}
                  {data.notas ? (
                    <Text fontSize="sm" color="gray.600" mt={2}>{data.notas}</Text>
                  ) : null}
                </Box>
              </VStack>
            ) : (
              <>
                <HStack gap={4} justify="center">
                  <Button
                    size="lg"
                    w="140px"
                    h="80px"
                    flexDirection="column"
                    colorPalette={asiste === true ? 'green' : 'gray'}
                    variant={asiste === true ? 'solid' : 'outline'}
                    onClick={() => setAsiste(true)}
                  >
                    <Icon boxSize={6} mb={1} asChild><FaCheck /></Icon>
                    <Text fontSize="sm">Sí, asisto</Text>
                  </Button>
                  <Button
                    size="lg"
                    w="140px"
                    h="80px"
                    flexDirection="column"
                    colorPalette={asiste === false ? 'red' : 'gray'}
                    variant={asiste === false ? 'solid' : 'outline'}
                    onClick={() => setAsiste(false)}
                  >
                    <Icon boxSize={6} mb={1} asChild><FaTimes /></Icon>
                    <Text fontSize="sm">No asisto</Text>
                  </Button>
                </HStack>

                {preguntaAutobus ? (
                  <Box bg="blue.50" p={3} rounded="lg" borderWidth="1px" borderColor="blue.100">
                    <HStack gap={2} mb={asiste === true ? 2 : 0}>
                      <Icon color="blue.500" asChild><FaBus /></Icon>
                      <Text fontSize="sm" fontWeight="600" color="blue.800">
                        Autobús
                      </Text>
                    </HStack>
                    {asiste !== true ? (
                      <Text fontSize="sm" color="gray.600">
                        La banda necesita saber si usarás autobús. Primero indica arriba si vas a asistir; si marcas
                        «Sí, asisto», podrás responder aquí.
                      </Text>
                    ) : (
                      <Field.Root display="flex" alignItems="center" justifyContent="space-between" gap={3}>
                        <Field.Label fontSize="sm" mb={0} flex={1}>
                          ¿Necesitas autobús?
                        </Field.Label>
                        <Switch
                          checked={necesitaAutobus}
                          onChange={(e) => setNecesitaAutobus(e.target.checked)}
                          colorPalette="blue"
                        />
                      </Field.Root>
                    )}
                  </Box>
                ) : null}

                <Field.Root>
                  <Field.Label fontSize="sm">Notas (opcional)</Field.Label>
                  <Textarea size="sm" value={notas} onChange={e => setNotas(e.target.value)} placeholder="Alguna observación..." rows={2} />
                </Field.Root>

                <Button
                  colorPalette="purple"
                  size="lg"
                  w="100%"
                  onClick={handleSubmit}
                  loading={submitting}
                  disabled={asiste === null}
                >
                  Enviar respuesta
                </Button>
              </>
            )}

            <Text fontSize="2xs" color="gray.400" textAlign="center">Powered by Cadencya</Text>
          </VStack>
        </Card.Body>
      </Card.Root>
    </Flex>
  );
}
